import { CommonErrorType } from '../../common/enums.js';
import {
  invitationSendingFailedMsg,
  invitationSentSuccessfullyMsg,
  savingTheDataFailedMsg,
  t,
  yourMessageWasSentMsg,
} from '../../common/i18n/index.js';
import { ONE_TIME_DEVICE_CHANGE_NOT_ALLOWED } from '../../common/utils/subscriptionUtils.js';
import { EppRedeemTerminateRequestType } from '../../generated/api/models.js';
import {
  addNotification,
  addNotificationWithoutTimer,
  clearNotifications,
} from '../../public/site/notificationStore.js';
import { TypeKeys } from '../actions/index.js';
import { removeDisplaySessionExpiredNotification } from '../common/localStorageUtils.js';
import type { ActionTypes } from '../actions/index.js';
import type { Middleware } from 'redux';

// Bridge: translates Redux actions dispatched by other, not-yet-migrated slices
export const notificationBridgeMiddleware: Middleware = () => next => (action: ActionTypes) => {
  const result = next(action);

  switch (action.type) {
    case TypeKeys.ADD_SUPPORT_CASE_FULFILLED: {
      if (!action.noSuccessNotification) {
        addNotification(action.successNotificationMsg ?? t.Q61D(yourMessageWasSentMsg), 'success');
      }
      break;
    }

    case TypeKeys.UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FULFILLED: {
      addNotification(t.UA9T('Change request was sent successfully.'), 'success');
      break;
    }

    case TypeKeys.UPSERT_BILLING_ACCOUNT_SCHEDULED_CHANGE_FAILED: {
      addNotification(t.JXYZ('Change request sending failed.'), 'error');
      break;
    }

    case TypeKeys.CHANGE_ADDONS_FULFILLED:
    case TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FULFILLED:
    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED: {
      addNotification(t.T29V('Change order successful'), 'success');
      break;
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FAILED: {
      if (
        action.errors?.[0].type === CommonErrorType.FORBIDDEN &&
        action.errors?.[0]?.message.includes(ONE_TIME_DEVICE_CHANGE_NOT_ALLOWED)
      ) {
        // We'll show a message dialog instead of notification
        break;
      }
      addNotification(t.RQA1(savingTheDataFailedMsg), 'warning');
      break;
    }

    case TypeKeys.SUSPEND_SUBSCRIPTION_FULFILLED: {
      addNotification(t.VOR4('Your subscription has now been temporarily deactivated.'), 'success');
      break;
    }

    case TypeKeys.RESUME_SUBSCRIPTION_FULFILLED: {
      addNotification(t.FUTC('Subscription successfully activated.'), 'success');
      break;
    }

    case TypeKeys.TERMINATE_SUBSCRIPTION_FULFILLED: {
      addNotification(
        t.OMP6(
          'Notice of termination has been received. You will receive a separate termination confirmation by email.'
        ),
        'success'
      );
      break;
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FULFILLED: {
      addNotification(t.EL9W('Link sent.'), 'success');
      break;
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FULFILLED: {
      addNotification(t.BFV5(invitationSentSuccessfullyMsg), 'success');
      break;
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED: {
      addNotification(t.RZIU(invitationSendingFailedMsg), 'error');
      break;
    }

    case TypeKeys.POST_EPP_REDEEM_TERMINATE_FULFILLED: {
      let notificationContent = '';
      if (
        action.request.requestType === EppRedeemTerminateRequestType.EMPLOYEE_REDEEM ||
        action.request.requestType === EppRedeemTerminateRequestType.COMPANY_REDEEM ||
        action.request.requestType === EppRedeemTerminateRequestType.EMPLOYEE_REDEEM_BY_ADMIN
      ) {
        notificationContent = t.KNS9('Redemption request sent successfully');
      } else if (action.request.requestType === EppRedeemTerminateRequestType.TERMINATE) {
        notificationContent = t.G4ZD('Your cancellation request was successfully sent');
      } else if (
        action.request.requestType === EppRedeemTerminateRequestType.DESTROYED ||
        action.request.requestType === EppRedeemTerminateRequestType.MISSING
      ) {
        notificationContent = t.DUFL('Your ‘device-destroyed’ request was sent');
      }
      addNotification(notificationContent, 'success');
      break;
    }

    case TypeKeys.FINALIZE_PAYMENT_FULFILLED: {
      addNotification(t.HA3D('Invoice paid successfully.'), 'success');
      break;
    }

    case TypeKeys.FINALIZE_PAYMENT_FAILED: {
      addNotification(t.NHMG('Payment of the invoice was interrupted, you were not charged.'), 'error');
      break;
    }

    case TypeKeys.OMALASKU_ORDER_SUBMITTED: {
      addNotification(t.D8WR('Change request sent successfully. Change may take up to one day to process.'), 'success');
      break;
    }

    case TypeKeys.OMALASKU_ORDER_FAILED_TO_SUBMIT: {
      addNotification(t.OQEQ('Failed to send change request. Try again later.'), 'error');
      break;
    }

    case TypeKeys.CLEAR_STATE: {
      clearNotifications();
      break;
    }

    case TypeKeys.SESSION_EXPIRED: {
      addNotificationWithoutTimer(
        {
          id: TypeKeys.SESSION_EXPIRED,
          text: t.SSJ9('Your session has expired. Please log in again to continue.'),
          type: 'info',
        },
        () => removeDisplaySessionExpiredNotification()
      );
      break;
    }

    default:
      break;
  }

  return result;
};
